import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';
import { FaUsers, FaUser, FaArrowRight, FaPlay } from 'react-icons/fa';
import { getMyWorkspaces } from '@/api/workspaceApi';
import { getMyWizardInstances } from '@/api/wizardApi';
import type { WorkspaceSummary, WizardInstance } from '@/types/workspace';

const wizardOptions = [
  {
    type: 'Leader' as const,
    icon: <FaUsers size={32} />,
    title: 'Leader Team Workspace',
    description:
      'Set up a structured workspace for your team. Creates a Security Group, M365 Group, SharePoint lists, and a Planner board — all connected.',
    recommended: true,
  },
  {
    type: 'Individual' as const,
    icon: <FaUser size={32} />,
    title: 'Individual Workspace',
    description:
      'Create your own personal workspace that ties to your leader\'s team. Track your own KPIs and goals linked to team objectives.',
    recommended: false,
  },
];

const WizardStart: React.FC = () => {
  const navigate = useNavigate();
  const [myWorkspaces, setMyWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [inProgress, setInProgress] = useState<WizardInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [ws, wi] = await Promise.all([getMyWorkspaces(), getMyWizardInstances()]);
        setMyWorkspaces(ws);
        setInProgress(wi);
      } catch {
        setError('Failed to load your workspaces.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-4" style={{ maxWidth: 900 }}>
      {/* Header */}
      <div className="mb-4">
        <h2 className="fw-bold mb-1">WorkOS Workspaces</h2>
        <p className="text-muted mb-0">
          Structured workspaces built on SharePoint, Teams, and Planner — set up once, use every day.
        </p>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* Existing workspaces */}
      {myWorkspaces.length > 0 && (
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body>
            <h5 className="mb-3">Your Workspaces</h5>
            {myWorkspaces.map(ws => (
              <div
                key={ws.workspaceId}
                className="d-flex align-items-center justify-content-between p-3 mb-2 rounded"
                style={{ background: 'var(--bs-light)' }}
              >
                <div>
                  <strong>{ws.displayName}</strong>
                  <span
                    className={`ms-2 badge ${ws.status === 'Active' ? 'bg-success' : ws.status === 'Provisioning' ? 'bg-warning text-dark' : 'bg-secondary'}`}
                  >
                    {ws.status}
                  </span>
                </div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => navigate(`/workspaces/${ws.workspaceId}`)}
                >
                  Open <FaArrowRight className="ms-1" />
                </Button>
              </div>
            ))}
          </Card.Body>
        </Card>
      )}

      {/* In-progress wizards (resume) */}
      {inProgress.length > 0 && (
        <Alert variant="info" className="mb-4">
          <strong>Resume in progress:</strong> You have a wizard that was started but not completed.{' '}
          <Button
            variant="link"
            className="p-0"
            onClick={() => navigate(`/workspaces/wizard/leader/${inProgress[0].wizardInstanceId}`)}
          >
            Continue where you left off
          </Button>
        </Alert>
      )}

      {/* What this does — brief explainer */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body className="p-4">
          <h5 className="mb-3">What does the wizard do?</h5>
          <Row>
            {[
              { step: '1', label: 'Creates a Security Group for your team — share once, everyone gets access' },
              { step: '2', label: 'Sets up (or links) an M365 Group and SharePoint site' },
              { step: '3', label: 'Builds 10 structured lists: Projects, Tasks, KPIs, Goals, Risks, Decisions…' },
              { step: '4', label: 'Creates a Planner board with standard buckets' },
            ].map(item => (
              <Col xs={12} md={6} key={item.step} className="mb-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: 32, height: 32, fontSize: 14, fontWeight: 700 }}
                  >
                    {item.step}
                  </div>
                  <p className="mb-0 small">{item.label}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>

      {/* Wizard type cards */}
      <h5 className="mb-3">Start a new workspace</h5>
      <Row className="g-3">
        {wizardOptions.map(opt => (
          <Col xs={12} md={6} key={opt.type}>
            <Card
              className={`h-100 border-0 shadow-sm ${opt.recommended ? 'border-start border-primary border-3' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/workspaces/wizard/${opt.type.toLowerCase()}/new`)}
            >
              <Card.Body className="p-4">
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div className="text-primary">{opt.icon}</div>
                  <div>
                    <h6 className="mb-0 fw-bold">{opt.title}</h6>
                    {opt.recommended && (
                      <span className="badge bg-primary bg-opacity-10 text-primary small">Start here</span>
                    )}
                  </div>
                </div>
                <p className="text-muted small mb-3">{opt.description}</p>
                <Button variant="primary" size="sm">
                  <FaPlay className="me-2" />
                  Start Wizard
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </Container>
  );
};

export default WizardStart;
